import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTelegramBack } from './hooks';

/**
 * A simple screen stack on top of the tabs: opening a profile or a chat pushes a
 * page; Telegram's back button (or a swipe/back link) pops it, like a native app.
 */
interface Nav {
  push: (key: string, node: ReactNode) => void;
  pop: () => void;
  depth: number;
}

const NavContext = createContext<Nav>({ push: () => undefined, pop: () => undefined, depth: 0 });

export function NavProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<Array<{ key: string; node: ReactNode }>>([]);
  const push = useCallback((key: string, node: ReactNode) => {
    setStack((s) => [...s.filter((x) => x.key !== key), { key, node }]);
  }, []);
  const pop = useCallback(() => setStack((s) => s.slice(0, -1)), []);
  const nav = useMemo(() => ({ push, pop, depth: stack.length }), [push, pop, stack.length]);
  useTelegramBack(stack.length ? pop : null);

  return (
    <NavContext.Provider value={nav}>
      {children}
      {stack.map((s, i) => (
        <div key={s.key} className="fixed inset-0 z-40 overflow-hidden bg-tg-bg" style={{ zIndex: 40 + i }}>
          {s.node}
        </div>
      ))}
    </NavContext.Provider>
  );
}

export function useNav() {
  return useContext(NavContext);
}
